import { useEffect, useState } from 'react';
import {
    HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
    ILogger,
    LogLevel,
} from '@microsoft/signalr';
import { useAuth } from '../auth/useAuth';
import { ExactLimitsBoard } from '../api/exactLimitsBoard';
import { rolloutHubUrl } from '../api/rollout';

const quietLogger: ILogger = {
    log(logLevel, message) {
        if (
            logLevel >= LogLevel.Warning &&
            !message.includes('stopped during negotiation')
        ) {
            console.warn(`[limits-hub] ${message}`);
        }
    },
};

/** How long a followed environment stays connected after its last screen went: a tab switch is not a goodbye. */
const LINGER_MS = 60000;

interface Follow {
    board?: ExactLimitsBoard;
    listeners: Set<(board: ExactLimitsBoard) => void>;
    hub?: HubConnection;
    stopTimer?: ReturnType<typeof setTimeout>;
}

// One connection per environment for the whole page, and the last board it pushed: every header, card and tab
// that shows the same environment's limits shares it, and one that mounts again shows the last board at once.
const follows = new Map<string, Follow>();
let tokenFactory: () => Promise<string> = () => Promise.resolve('');

const keyOf = (customer: string, environment: string) =>
    `${customer}/${environment}`.toLowerCase();

const connect = (customer: string, environment: string, follow: Follow) => {
    const hub = new HubConnectionBuilder()
        .withUrl(rolloutHubUrl, { accessTokenFactory: () => tokenFactory() })
        .withAutomaticReconnect()
        .configureLogging(quietLogger)
        .build();

    const publish = (board: ExactLimitsBoard | null | undefined) => {
        if (!board || board.error) return;
        follow.board = board;
        follow.listeners.forEach((l) => l(board));
    };

    const start = async () =>
        publish(
            await hub.invoke<ExactLimitsBoard>(
                'followLimits',
                customer,
                environment
            )
        );

    hub.on(
        'exactLimits',
        (message: {
            customer: string;
            environment: string;
            board: ExactLimitsBoard;
        }) => {
            if (
                message.customer === customer &&
                message.environment === environment
            )
                publish(message.board);
        }
    );
    hub.onreconnected(() => start().catch(() => undefined));
    hub.start()
        .then(() => (follow.hub === hub ? start() : undefined))
        .catch(() => undefined);

    follow.hub = hub;
};

const release = (key: string, follow: Follow) => {
    if (follow.listeners.size > 0) return;
    follow.stopTimer = setTimeout(() => {
        if (follow.listeners.size > 0) return;
        const hub = follow.hub;
        follow.hub = undefined;
        if (hub && hub.state !== HubConnectionState.Disconnected)
            hub.stop().catch(() => undefined);
        // The board stays: shown at once, though possibly a minute old, until the new connection answers.
    }, LINGER_MS);
    follows.set(key, follow);
};

/**
 * Exact's limits of one environment, pushed by the server as they move: the board every caller
 * with the connector's token keeps up to date. No polling from the page.
 *
 * Shared per environment: moving between tabs keeps the last board on screen instead of starting
 * empty and connecting again.
 */
export const useLiveExactLimits = (customer?: string, environment?: string) => {
    const { getAccessTokenSilently } = useAuth();
    tokenFactory = getAccessTokenSilently;
    const key = customer && environment ? keyOf(customer, environment) : '';
    const [board, setBoard] = useState<ExactLimitsBoard | undefined>(() =>
        key ? follows.get(key)?.board : undefined
    );

    useEffect(() => {
        if (!customer || !environment) {
            setBoard(undefined);
            return;
        }

        const follow = follows.get(key) ?? { listeners: new Set() };
        follows.set(key, follow);
        setBoard(follow.board);

        if (follow.stopTimer) clearTimeout(follow.stopTimer);
        follow.stopTimer = undefined;

        const listener = (next: ExactLimitsBoard) => setBoard(next);
        follow.listeners.add(listener);

        if (!follow.hub) connect(customer, environment, follow);

        return () => {
            follow.listeners.delete(listener);
            release(key, follow);
        };
    }, [customer, environment, key]);

    return board;
};
